import { useRoute } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { apiJson } from "@/lib/api";
import { PageHeader } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowLeft, Building2, FileText, Briefcase } from "lucide-react";
import { useLocation } from "wouter";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

export default function AdvertiserDetailPage() {
    const [, params] = useRoute("/admin/advertisers/:id");
    const [, setLocation] = useLocation();
    const id = params?.id ? parseInt(params.id) : 0;

    // Fetch advertiser details
    const { data: advertiser, isLoading } = useQuery<{
        id: number;
        name: string;
        address?: string | null;
        contactName?: string | null;
        contactEmail?: string | null;
        contactPhone?: string | null;
        isActive: boolean;
        clientId?: number | null;
        clientName?: string | null;
    }>({
        queryKey: ["advertiser", id],
        queryFn: () => apiJson(`/api/advertisers/${id}`),
        enabled: !!id,
    });

    // Fetch bookings for this advertiser
    const { data: bookings = [], isLoading: bookingsLoading } = useQuery<any[]>({
        queryKey: ["bookings", "advertiser", id],
        queryFn: () => apiJson(`/api/bookings?advertiserId=${id}`),
        enabled: !!id,
    });

    // Fetch campaigns for this advertiser
    const { data: campaignsData, isLoading: campaignsLoading } = useQuery<{ data: any[] }>({
        queryKey: ["campaigns", "advertiser", id],
        queryFn: () => apiJson(`/api/campaigns?advertiserId=${id}`),
        enabled: !!id,
    });
    const campaigns = campaignsData?.data || [];

    if (isLoading) {
        return (
            <div className="flex h-[50vh] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
        );
    }

    if (!advertiser) {
        return (
            <div className="p-6">
                <div className="text-center">
                    <h2 className="text-xl font-semibold">Advertiser not found</h2>
                    <Button variant="link" onClick={() => setLocation("/admin/advertisers")}>
                        Back to Advertiser List
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            <Button
                variant="ghost"
                className="mb-2 pl-0 hover:pl-2 transition-all"
                onClick={() => setLocation("/admin/advertisers")}
            >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Advertisers
            </Button>

            <PageHeader
                title={advertiser.name}
                description={
                    <div className="flex items-center gap-2 mt-1">
                        <Badge variant={advertiser.isActive ? "default" : "secondary"}>
                            {advertiser.isActive ? "Active" : "Inactive"}
                        </Badge>
                        {advertiser.clientName && (
                            <>
                                <span className="text-muted-foreground">•</span>
                                <a
                                    href={`/admin/clients/${advertiser.clientId}`}
                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                    {advertiser.clientName}
                                </a>
                            </>
                        )}
                    </div>
                }
            />

            <Tabs defaultValue="overview" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="bookings">
                        Bookings
                        <Badge variant="secondary" className="ml-2 h-5 min-w-[20px] px-1">
                            {bookings.length}
                        </Badge>
                    </TabsTrigger>
                    <TabsTrigger value="campaigns">
                        Campaigns
                        <Badge variant="secondary" className="ml-2 h-5 min-w-[20px] px-1">
                            {campaigns.length}
                        </Badge>
                    </TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">Contact Details</CardTitle>
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-sm space-y-2 mt-2">
                                    <div>
                                        <span className="font-semibold block text-xs text-muted-foreground">Name</span>
                                        {advertiser.contactName || "Not recorded"}
                                    </div>
                                    <div>
                                        <span className="font-semibold block text-xs text-muted-foreground">Email</span>
                                        {advertiser.contactEmail ? (
                                            <a href={`mailto:${advertiser.contactEmail}`} className="text-blue-600 hover:underline">
                                                {advertiser.contactEmail}
                                            </a>
                                        ) : "Not recorded"}
                                    </div>
                                    <div>
                                        <span className="font-semibold block text-xs text-muted-foreground">Phone</span>
                                        {advertiser.contactPhone || "Not recorded"}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">Address</CardTitle>
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-sm mt-2 whitespace-pre-wrap">
                                    {advertiser.address || "Not recorded"}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Bookings Tab */}
                <TabsContent value="bookings">
                    <Card>
                        <CardHeader>
                            <CardTitle>Advertiser Bookings</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {bookingsLoading ? (
                                <div className="flex items-center justify-center p-8">
                                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                </div>
                            ) : bookings.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Booking Name</TableHead>
                                            <TableHead>Cycle</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead className="text-right">Value</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {bookings.map((booking: any) => (
                                            <TableRow key={booking.id}>
                                                <TableCell className="font-medium">
                                                    <a
                                                        href={`/admin/bookings/${booking.id}`}
                                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                                    >
                                                        {booking.name || `Booking #${booking.id}`}
                                                    </a>
                                                </TableCell>
                                                <TableCell>
                                                    {booking.cycleName || (booking.cycleId ? `Cycle ${booking.cycleId}` : "—")}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant={booking.status === "confirmed" ? "default" : "secondary"}>
                                                        {booking.status || "Draft"}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {booking.bookingValue ? `€${Number(booking.bookingValue).toLocaleString()}` : "—"}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
                                    <Briefcase className="h-10 w-10 mb-4 opacity-20" />
                                    <p>No bookings for this advertiser yet.</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Campaigns Tab */}
                <TabsContent value="campaigns">
                    <Card>
                        <CardHeader>
                            <CardTitle>Advertiser Campaigns</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {campaignsLoading ? (
                                <div className="flex items-center justify-center p-8">
                                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                </div>
                            ) : campaigns.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Campaign Name</TableHead>
                                            <TableHead>Cycle</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Booking</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {campaigns.map((campaign: any) => (
                                            <TableRow key={campaign.id}>
                                                <TableCell className="font-medium">
                                                    <a
                                                        href={`/campaigns/${campaign.id}`}
                                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                                    >
                                                        {campaign.name}
                                                    </a>
                                                </TableCell>
                                                <TableCell>
                                                    {campaign.cycleName || (campaign.cycleId ? `Cycle ${campaign.cycleId}` : "—")}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant={campaign.status === "active" ? "default" : "secondary"}>
                                                        {campaign.status || "Draft"}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    {campaign.bookingId ? (
                                                        <a
                                                            href={`/admin/bookings/${campaign.bookingId}`}
                                                            className="text-blue-600 hover:text-blue-800 hover:underline"
                                                        >
                                                            {campaign.bookingName || `#${campaign.bookingId}`}
                                                        </a>
                                                    ) : (
                                                        <span className="text-muted-foreground italic">Manual</span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
                                    <FileText className="h-10 w-10 mb-4 opacity-20" />
                                    <p>No campaigns for this advertiser yet.</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
